import React from 'react'
import styled from 'styled-components'
import { DocumentSource, LandUsePlanDocument } from '@popety-io/types'

export interface DocumentChipsTranslations {
  sourceLabels?: Partial<Record<DocumentSource, string>>
}

// Default source display labels
const DEFAULT_SOURCE_LABELS: Record<DocumentSource, string> = {
  lupa: 'Local Plan',
  lupc: 'Cantonal Plan',
  lupo: 'Overlay Plan',
  lup: 'Communal Plan',
  region: 'Cantonal',
  country: 'Federal',
}

interface DocumentChipsProps {
  documentsBySource: Partial<Record<DocumentSource, LandUsePlanDocument[]>>
  precedenceOrder: DocumentSource[]
  totalCount: number
  onChipClick: (source: DocumentSource) => void
  translations?: DocumentChipsTranslations
}

export const DocumentChips: React.FC<DocumentChipsProps> = ({
  documentsBySource,
  precedenceOrder,
  totalCount,
  onChipClick,
  translations,
}) => {
  // Merge source labels with defaults
  const sourceLabels: Record<DocumentSource, string> = {
    ...DEFAULT_SOURCE_LABELS,
    ...translations?.sourceLabels,
  }
  if (precedenceOrder.length === 0 || totalCount === 0) {
    return null
  }

  // Get the first source (highest precedence) for the "+X more" chip
  const firstSource = precedenceOrder[0]
  const firstSourceDocs = documentsBySource[firstSource] || []
  const additionalFromFirst = Math.max(0, firstSourceDocs.length - 2)

  return (
    <ChipsContainer>
      {/* Show "+X more" chip for first source if there are more than 2 documents */}
      {additionalFromFirst > 0 && (
        <Chip
          onClick={() => onChipClick(firstSource)}
          aria-label={`View ${additionalFromFirst} more ${sourceLabels[firstSource]} documents`}
        >
          +{additionalFromFirst} {sourceLabels[firstSource]}
        </Chip>
      )}

      {/* Show chips for all other sources in precedence order */}
      {precedenceOrder.slice(1).map((source) => {
        const count = documentsBySource[source]?.length || 0
        if (count === 0) return null

        return (
          <Chip
            key={source}
            onClick={() => onChipClick(source)}
            aria-label={`View ${count} ${sourceLabels[source]} documents`}
          >
            {sourceLabels[source]}: {count}
          </Chip>
        )
      })}
    </ChipsContainer>
  )
}

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

const Chip = styled.button`
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`
